import { createFileRoute, Link } from "@tanstack/react-router";
import { Rocket } from "lucide-react";

export const Route = createFileRoute("/careplanterms")({
  head: () => ({
    meta: [
      { name: "robots", content: "noindex, nofollow" },
      { title: "Care Plan Terms of Purchase — Three Day Launch" },
      { name: "description", content: "Terms of Purchase for the Three Day Launch Website Hosting + Care Plan subscription." },
      { property: "og:title", content: "Care Plan Terms of Purchase — Three Day Launch" },
      { property: "og:description", content: "Terms of Purchase for the Three Day Launch Website Hosting + Care Plan subscription." },
      { property: "og:url", content: "https://threedaylaunch.com/careplanterms" },
    ],
    links: [{ rel: "canonical", href: "https://threedaylaunch.com/careplanterms" }],
  }),
  component: TermsOfPurchasePage,
});

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h2 className="text-2xl font-semibold mb-3">{title}</h2>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

function TermsOfPurchasePage() {
  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border">
        <div className="mx-auto max-w-4xl px-6 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-primary-gradient flex items-center justify-center">
              <Rocket className="h-4 w-4 text-white" />
            </div>
            <span className="font-bold">Three Day Launch<sup className="text-[0.6em] ml-0.5">®</sup></span>
          </Link>
          <Link to="/" className="text-sm text-muted-foreground hover:text-foreground">← Back home</Link>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-6 py-16">
        <h1 className="text-4xl md:text-5xl font-bold tracking-tight">
          Terms of Purchase - Website Care Plan
        </h1>
        <p className="mt-4 text-sm text-muted-foreground">Last Updated: April 24, 2026</p>

        <div className="mt-10 space-y-10 text-foreground/90 leading-relaxed">
          <Section title="1. Overview">
            <p>
              By purchasing a Website Hosting + Care Plan subscription (“Service”) from Three Day Launch (“Company,” “we,” “us,” or “our”), you (“Client,” “you,” or “your”) agree to the following Terms of Purchase.
            </p>
            <p>
              This Service includes ongoing website hosting, maintenance, and support as outlined on the checkout page at the time of purchase.
            </p>
          </Section>

          <Section title="2. Subscription & Billing">
            <p>This is a recurring subscription service billed on a bi-weekly basis.</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Billing will automatically renew unless canceled prior to the next billing cycle</li>
              <li>You authorize us to charge your payment method on file for all recurring fees</li>
              <li>Prices are subject to change with at least 30 days’ notice</li>
            </ul>
            <p>Failure to process payment may result in suspension or termination of your website hosting and services.</p>
          </Section>

          <Section title="3. Scope of Services">
            <p>Your plan may include (based on your selected tier):</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Website hosting</li>
              <li>Platform maintenance</li>
              <li>Security updates</li>
              <li>Design/content updates</li>
              <li>Technical support</li>
            </ul>
            <p><strong>Important:</strong></p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Services are limited to what is explicitly included in your plan</li>
              <li>Requests outside of scope may require an additional fee or upgrade</li>
              <li>We reserve the right to define what qualifies as “minor” updates.</li>
            </ul>
          </Section>

          <Section title="4. Client Responsibilities">
            <p>You agree to:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Provide accurate and timely content, login access, and materials when requested</li>
              <li>Maintain appropriate backups of your own content</li>
              <li>Use the website in compliance with all applicable laws and platform policies</li>
            </ul>
            <p>You are solely responsible for the content published on your website.</p>
          </Section>

          <Section title="5. No Guarantees">
            <p>While we strive to provide reliable service:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>We do not guarantee uninterrupted or error-free website performance</li>
              <li>We are not responsible for outages caused by third-party platforms (e.g., Lovable, hosting providers, domain registrars, etc.)</li>
              <li>We do not guarantee specific business results (e.g., traffic, leads, or sales)</li>
            </ul>
          </Section>

          <Section title="6. Cancellations">
            <p>You may cancel your subscription at any time by providing written notice.</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Cancellation will take effect at the end of your current billing cycle</li>
              <li>No partial refunds will be issued for unused time</li>
            </ul>
            <p>Upon cancellation:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Your website hosting and services will be discontinued</li>
              <li>You are responsible for acquiring your own website hosting with Lovable prior to termination</li>
            </ul>
          </Section>

          <Section title="7. Refund Policy">
            <p>Due to the nature of digital services and ongoing access:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>All payments are non-refundable</li>
              <li>No refunds will be issued for partial subscription periods</li>
              <li>No refunds will be issued for failure to use the Service</li>
            </ul>
          </Section>

          <Section title="8. Suspension & Termination">
            <p>We reserve the right to suspend or terminate your access to the Service if:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Payment fails or is overdue</li>
              <li>You violate these Terms</li>
              <li>Your website contains unlawful, harmful, or prohibited content</li>
            </ul>
            <p>Termination may result in removal of your website from hosting.</p>
          </Section>

          <Section title="9. Intellectual Property">
            <p>You retain ownership of your website content.</p>
            <p>However:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Any templates, systems, or proprietary processes provided by us remain our intellectual property</li>
              <li>You may not copy, resell, or redistribute our systems without permission</li>
            </ul>
          </Section>

          <Section title="10. Limitation of Liability">
            <p>To the fullest extent permitted by law:</p>
            <p>We are not liable for:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Loss of revenue, sales, or business opportunities</li>
              <li>Data loss or website downtime</li>
              <li>Third-party platform failures</li>
            </ul>
            <p>Our total liability shall not exceed the amount paid by you in the previous 30 days.</p>
          </Section>

          <Section title="11. Indemnification">
            <p>You agree to indemnify and hold harmless Three Day Launch from any claims, damages, or legal actions arising from:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Your website content</li>
              <li>Your business practices</li>
              <li>Your use of the Service</li>
            </ul>
          </Section>

          <Section title="12. Force Majeure">
            <p>We shall not be liable for failure or delay in performance due to causes beyond our reasonable control, including but not limited to:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Natural disasters</li>
              <li>Internet outages</li>
              <li>Platform failures</li>
              <li>Acts of God</li>
              <li>Government actions</li>
              <li>Labor disputes</li>
            </ul>
          </Section>

          <Section title="13. Modifications">
            <p>We reserve the right to update these Terms at any time.</p>
            <p>Continued use of the Service constitutes acceptance of any updated Terms.</p>
          </Section>

          <Section title="14. Governing Law">
            <p>These Terms shall be governed by the laws of the State of Illinois, without regard to conflict of law principles.</p>
          </Section>

          <Section title="15. Support Response Time">
            <p>Client inquiries submitted via email will receive a response within 24 business hours (Monday–Friday), unless otherwise communicated in advance regarding scheduled time away from the office.</p>
            <p>“Response time” refers to acknowledgment of your request, not full completion.</p>
          </Section>

          <Section title="16. Business Hours">
            <p>Support and task fulfillment are provided during standard business hours:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Monday–Friday</li>
              <li>Excluding weekends and U.S. federal holidays</li>
            </ul>
          </Section>

          <Section title="17. Contact">
            <p>For questions, please contact:</p>
            <p>
              Three Day Launch<br />
              <a href="mailto:hello@threedaylaunch.com" className="underline">hello@threedaylaunch.com</a>
            </p>
          </Section>
        </div>
      </main>
    </div>
  );
}
